// English source copy for the landing page. Every other locale file mirrors this shape.
// Conventions: "\n" = line break; {n} = number of Agentic Apps; {nWord} = that number spelled out
// (English only); {email} = contact email. Product names (Estha, Chat Apps, Agentic Apps,
// Quick Bot, Playground, My Org, My Org Admin, Estha Server, Mac Studio Cluster, MLX) are never translated.

const en = {
  nav: {
    chat: 'Chat Apps',
    agentic: 'Agentic Apps',
    build: 'Create App',
    org: 'My Org',
    pricing: 'Pricing',
    privacy: 'Privacy',
    contact: 'Contact',
    language: 'Language',
  },
  hero: {
    // Rendered as: pre + <mark>mark</mark> + post; "\n" = line break.
    titlePre: "Your ",
    titleMark: "local",
    titlePost: " AI\npowerhouse.",
    sub: "Turn expert knowledge, spreadsheets and meetings into answers, charts and decks. Nothing leaves your Mac.",
    watch: "Watch the 2-min film",
    pill: "Latest release",
    chip: "Wi‑Fi off · 0 bytes sent",
    badges: { offline: "Works offline", server: "Your server, not ours", free: "Free to start" },
    reqs: 'Requires a Mac with Apple Silicon · 16GB memory minimum · 32GB+ unlocks the larger recommended models',
  },
  stats: {
    free: 'Free',
    freeSub: 'All Chat Apps, model downloads and updates — always',
    chatApps: 'Chat Apps',
    agenticApps: 'Agentic Apps',
    bytesSub: 'bytes sent to the cloud when AI runs on this Mac',
    langsSub: 'interface languages, from English to 日本語',
  },
  film: {
    kicker: 'The film',
    title: 'See Estha in action.',
    watchOnYouTube: 'Watch on YouTube',
    trouble: 'Trouble playing? Watch on YouTube ↗',
  },
  overview: {
    kicker: 'One app, four ways to work',
    title: 'From chat, to agents,\nto apps your whole team uses.',
    explore: 'Explore',
    chat: { title: 'Chat Apps', desc: 'AI experts grounded in curated knowledge bases — textbooks, playbooks and domain expertise.', tag: 'Always free' },
    agentic: { title: 'Agentic Apps', desc: 'Task-oriented AI agents that handle complex work end-to-end.', tag: '{n} and counting' },
    build: { title: "Create App", desc: 'Turn your own materials into a Chat App with Quick Bot, or compose flows on the Playground canvas.', tag: 'Quick Bot · Playground' },
    org: { title: 'My Org', desc: "Your organization's own branded app space, with reviewed apps and controlled access.", tag: 'Enterprise' },
  },
  chat: {
    kicker: 'Chat Apps',
    title: 'Expert AI, grounded in real knowledge.',
    freePill: 'Always free',
    lede: 'Each Chat App ships with its own knowledge base, indexed right on your Mac.',
    feats: {
      kb: { title: 'Local knowledge bases', desc: 'Installing an app creates its knowledge base locally — answers come from curated material.' },
      files: { title: 'Bring your own files', desc: 'Attach images, PDF, DOCX, TXT or MD — long documents use smart retrieval.' },
      browse: { title: 'Browse, pin, favorite', desc: 'Categories from Assessment to Career, NEW tags for fresh apps, ⌘K to search everything.' },
    },
  },
  agentic: {
    kicker: 'Agentic Apps',
    title: '{n} Agentic Apps.\nAnd counting.',
    lede: 'From a spreadsheet, a PDF, a recording or an idea to a finished chart, deck, document or prototype — privately on your Mac.',
    seeThem: 'See them at work',
  },
  build: {
    kicker: 'Create App',
    title: 'Make your own Chat App.\nRight on your Mac.',
    lede: 'Turn your own files into a Chat App — indexed locally, published when you’re ready.',
    quick: {
      badge: 'Recommended',
      desc: 'Upload a knowledge base and write a system prompt, then publish. Great for turning materials into a Q&A bot fast.',
      items: ['One-page form, done in a few steps', 'Knowledge-base Q&A out of the box', 'Built-in prompt templates for inspiration'],
    },
    advanced: {
      title: 'Advanced · Playground',
      desc: 'Visual node editor to freely compose multiple nodes, branches, AB tests and keyword routing.',
      items: ['Node canvas', 'Multi-node / branch / AB test', 'Fine-grained fields & debugging'],
    },
    guideTitle: 'A local AI building guide sits beside you.',
    guideText: 'Tell it what you want the app to do and it walks you through which nodes to drag and how to connect them. Building is included with Premium.',
  },
  private: {
    kicker: 'Private by design',
    title: "No cloud for your data.\nNo tracking of your work.",
    lede: "You decide where the AI runs. Estha picks the right model for each feature by default — and you can switch models any time in the app.",
    boundary: "Data boundary = your network",
    local: { title: 'On this Mac', desc: "Fully offline, nothing leaves this machine. Runs models using this computer's memory.", best: 'Best for Macs with plenty of memory' },
    server: { title: 'On my Estha Server', desc: 'Offload the heavy work to a more powerful Mac on your network, keeping this Mac light.', best: 'Best for laptops and Macs with limited memory' },
    cluster: { desc: 'Connect to a Mac Studio cluster to offload heavy inference while keeping your data on-prem.', best: 'Auto-discovers Estha Studio Master nodes on your network' },
    models: {
      every: 'Runs on every supported Mac',
      whisper: "Whisper for transcription",
      recommended: 'The recommended models',
      larger: "Larger Qwen and Gemma models",
      vision: "Vision models for images",
      open: 'Open models, tuned for Apple Silicon',
      openItems: ['Download and switch models in-app', 'Dedicated coder & embedding models', 'Model downloads always free'],
    },
  },
  org: {
    kicker: 'My Org',
    title: "Your organization's own AI app space.",
    lede: 'A branded section in Estha holding the apps your organization has reviewed for its people.',
    signIn: 'Sign in to My Org Admin',
    talk: 'Talk to the Estha team',
    feats: {
      review: { title: 'Review before publish', desc: 'Every new app, update or removal goes to an admin. Rejections always come with written feedback.' },
      access: { title: 'Access by group or person', desc: 'Decide exactly who sees each app — by department, team or individual.' },
      brand: { title: 'Your brand, in the app', desc: 'Organization name, welcome message, logo, banner and theme color — previewed before it goes live.' },
    },
  },
  pricing: {
    kicker: 'Membership',
    title: "Start free. Unlock everything when you're ready.",
    free: { name: 'Free', sub: 'Always free.', items: ["All Chat Apps", 'Document Processing, free forever', 'Model downloads, software and updates — always free'] },
    premium: {
      name: 'Premium', flag: '14-day free trial', firstYear: 'first year',
      sub: 'Then $30/year. Start with a 14-day free trial after verifying your email.',
      items: ['Everything in Free', 'All current and future Agentic Apps', 'Chat App builder — create, edit, preview, save, and publish local chatbots'],
      cta: 'Start the trial in the app',
    },
    enterprise: {
      name: 'Enterprise', price: "Let's talk", sub: 'For organizations rolling Estha out to teams.',
      items: ['Everything in Premium', 'Custom Chat Apps and Agentic Apps built for your team', 'Estha Server app capability', 'My Org app space and My Org Admin'],
    },
  },
  faq: {
    title: 'Good to know\nbefore you download.',
    spec: { chip: 'Chip', memory: 'Memory', memMin: '16GB minimum', memRec: '32GB+ for the recommended models', languages: 'Languages', langsVal: '13 interface languages', version: 'Latest version' },
    qa: [
      { q: 'Does anything leave my Mac?', a: "When AI runs on this Mac, it's fully offline — nothing leaves the machine. If you choose an Estha Server or a Mac Studio cluster, work is sent only to that machine on your own network." },
      { q: "What needs an internet connection?", a: "Only a few things: signing in and verifying your email for the Premium trial, downloading models, and checking for updates. Your prompts, files and results are never uploaded — once a model is downloaded, the AI works fully offline." },
      { q: "What's free, and what's Premium?", a: 'All Chat Apps, Document Processing, model downloads and updates are free. Premium unlocks every current and future Agentic App plus the Chat App builder, with a 14-day free trial.' },
      { q: 'Which AI models does it use?', a: "Leading open-source models such as Qwen and Gemma, plus Whisper for transcription, running through Apple's MLX engine. You download and switch them inside the app." },
      { q: 'Can I make my own app?', a: "Yes. Use Quick Bot to turn up to three files into a knowledge-base Chat App in a few steps, or the Playground's node canvas for branches, AB tests and quizzes. A local building guide helps along the way." },
      { q: 'How do organizations roll it out?', a: "With Enterprise, admins manage users and groups in My Org Admin, review every app before it's published, and members get a branded My Org space inside Estha. Contact {email}." },
    ],
  },
  final: {
    title: 'Ready to run AI locally?',
    reqs: 'Runs on any Mac with Apple Silicon · 16GB RAM minimum',
  },
  footer: {
    tagline: 'Your local AI powerhouse — on-device, powered by MLX, private by design.',
    product: 'Product', createApp: 'Create App', pricing: 'Pricing',
    orgs: "My Org", signIn: 'Sign in to My Org Admin',
    security: "Security", changelog: "Changelog", docs: "Docs", support: "Support",
    company: 'Company', privacy: 'Privacy Policy', terms: 'Terms of Service',
    rights: '© 2026 Estha. All rights reserved.',
  },
  proof: { press: "As covered in", voices: "What teams say" },
  contact: {
    title: "Email the Estha team", copy: "Copy email address", copied: "Copied",
    gmail: "Write in Gmail", mailApp: "Open in Mail app",
    subject: "Estha for Mac inquiry",
  },
  share: {
    button: "Share", title: "Share Estha",
    sub: "Pick a card, then post it — or just send the link.",
    style: "Card", size: "Size",
    templates: { hero: "Estha", agentic: "Agentic Apps", private: "Private" },
    landscape: "Landscape", portrait: "Portrait",
    shareImage: "Share image…", download: "Download image", copyImage: "Copy image", copied: "Copied",
    copyLink: "Copy link", link: "Link", postTo: "Post to",
    email: "Email",
    text: "Estha runs open-source AI models entirely on your Mac — private, offline and free to download.",
    cardCta: "Free download for Macs with Apple Silicon",
    close: "Close",
  },
  // The Agentic Apps — official strings from the Estha for Mac app (common.json → agenticApps).
  apps: {
    dataAnalyst: { name: 'Data Analyst', description: 'Upload CSV/Excel files and ask questions in natural language. Automatically generates charts, statistical analysis, and data insights — all processed locally on your Mac.', tags: ['CSV / Excel', 'Auto Charts', 'Natural Language Query', 'EN / 中文'] },
    pptMaker: { name: 'PPT Maker', description: 'Describe your presentation topic and get a professionally structured PowerPoint deck. Multi-step AI pipeline generates outline, per-slide content, and polished .pptx file.', tags: ['.pptx Output', 'Auto Layout', 'Multi-step Pipeline'] },
    pdfReader: { name: 'PDF Reader', description: 'Open a PDF and chat with it. Every answer is grounded in the document with tap-to-verify citations that jump to the exact source passage — all processed locally on your Mac.', tags: ['PDF Q&A', 'Verifiable Citations', 'Selection Ask', '100% Local'] },
    documentProcessing: { name: 'Document Processing', description: 'Your all-in-one document expert. Draft blog posts, social media copy, legal clauses, and business documents from scratch. Edit and optimize existing files, translate across languages, and review content for quality — all privately on your Mac.', tags: ['Drafting & Writing', 'Editing & Optimization', 'Translation', 'Review & Audit'] },
    meetingIntelligence: { name: 'Meeting Intelligence', description: 'Record, transcribe, and analyze meetings with AI. Automatically generate meeting minutes, action items, and key decisions — with complete privacy, no audio ever leaves your device.', tags: ['Transcription', 'Meeting Minutes', 'Action Items', '100% Private'] },
    vibeCoding: { name: 'Vibe Coding: Prototype', description: 'Create interactive previews with AI. Describe an interface in plain language, get a clickable prototype, and keep iterating on it. Outputs one self-contained .html file, generated entirely on your Mac.', tags: ['Single .html File', 'Interactive Preview', 'Point-to-Edit', '100% Local'] },
    translate: { name: "Translate", description: "Translate text and Word, text or Markdown documents with a shared glossary that keeps your terms consistent — all processed locally on your Mac.", tags: ["Text & Documents", ".docx / .txt / .md", "Glossary", "100% Local"] },
  },
  // Screenshot step buttons and captions. Titles shown next to captions are app UI names and stay in English.
  screens: {
    chat: [
      { label: 'Browse the store', caption: 'Browse, install, pin — always free' },
      { label: 'Install', caption: 'Knowledge bases are created locally on install' },
      { label: 'Ask', caption: 'Answers grounded in its playbook' },
    ],
    build: [
      { label: 'Create App', caption: 'Quick Bot or Advanced — both chat-ready' },
      { label: 'Quick Bot', caption: 'Upload a knowledge base. Write a prompt. Publish.' },
      { label: 'Playground', caption: 'Nodes, branches and knowledge — visually' },
      { label: 'Published', caption: 'Published and ready to chat' },
    ],
    analyst: [
      { label: 'Ask a question', caption: 'Ask your data in natural language' },
      { label: 'Analyzing', caption: 'Analyzed on your Mac' },
      { label: 'Chart & insight', caption: 'The answer, the chart and how it was done' },
    ],
    ppt: [
      { label: 'Set up', caption: 'Topic, slides, language, tone, theme' },
      { label: 'Review outline', caption: 'Edit the outline before anything is generated' },
      { label: 'Preview deck', caption: 'A finished, editable .pptx — on your Mac' },
    ],
    pdf: [
      { label: 'Open a PDF', caption: 'Papers, reports, manuals and scanned docs' },
      { label: 'Reading', caption: 'Read locally, page by page' },
      { label: 'Cited answer', caption: 'Every answer points to the exact clause' },
    ],
    doc: [
      { label: 'Select a passage', caption: 'Select text and say what you want' },
      { label: 'Rewriting', caption: 'Rewritten right in the document' },
      { label: 'Accept or reject', caption: 'Rewrite, polish, translate — accept or reject' },
    ],
    meeting: [
      { label: 'Record', caption: 'Live transcript, on this Mac' },
      { label: 'Choose outputs', caption: 'Pick what to generate' },
      { label: 'Summary', caption: 'Decisions and action items, written for you' },
      { label: 'Action items', caption: 'Owners and due dates, with evidence' },
    ],
    vibe: [
      { label: 'Describe', caption: 'Describe an interface in plain language' },
      { label: 'Generating', caption: 'Built entirely on this Mac' },
      { label: 'Prototype', caption: 'Clickable, versioned, one .html file' },
      { label: 'Point-to-edit', caption: 'Select any part and say what to change' },
    ],
    translate: [
      { label: "Translate text", caption: "Purple words come from your glossary" },
      { label: "Translate a document", caption: ".docx, .txt or .md — the original stays unchanged" },
      { label: "Glossary", caption: "Your glossary keeps every term consistent" },
    ],
    private: [
      { label: 'On this Mac', caption: 'Fully offline. Nothing leaves this machine.' },
      { label: 'Mac Studio Cluster', caption: 'Heavier models — on-prem' },
    ],
    org: [
      { label: 'Org app space', caption: 'Branded space, apps built for the team' },
      { label: 'Enterprise plan', caption: 'Members open only the apps you grant' },
    ],
  },
};

export default en;
export type LandingDict = typeof en;
